import H3 from '@base/H3/H3';
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import RadioButton from '@base/RadioButton/RadioButton';
import H5 from '@base/H5/H5';
import Dropdown from '@comp/Dropdown/Dropdown';
import { emptyList } from '@constants/empty';
import { useState } from 'react';
import useDoubtConfig from '@api/hooks/goal/custom-doubts/useDoubtConfig';
import useSubjectAndLanguages from '@api/hooks/goal/custom-doubts/useSubjectAndLanguages';

const StyH3 = styled(H3)`
  grid-column: span 10;
  margin-top: ${Spacings.SPACING_11B};
`;

const ImageWrapper = styled.div`
  margin-top: ${Spacings.SPACING_8B};
  width: 464px;
  height: 262.93px;
  position: relative;
  display: block;
  overflow: hidden;
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_1B};
  ${mediaQuery.uptoMobile} {
    width: 360px;
    height: 203px;
  }
`;

const DountImage = styled.img`
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const BackgroundWrapper = styled.div`
  width: 240px;
  height: 136px;
  background-image: url('${(props) => props.bgImageSrc}');
  display: block;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  z-index: 1;
  filter: blur(64px);
  top: 0;
  left: 0;
`;

const StyH5Info = styled(H5)`
  grid-column: span 10;
  margin-bottom: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_5B};
`;

const DoubtImageWrapper = ({ content }) => {
  return (
    <ImageWrapper>
      <BackgroundWrapper bgImageSrc={content} />
      <DountImage src={content} alt="doubt-image" />
    </ImageWrapper>
  );
};

const PreferenceWrapper = styled.div`
  border-radius: ${Spacings.SPACING_2B};
  border: ${({ isSelected }) => {
    if (isSelected) {
      return '1px solid var(--color-text-primary)';
    }
    return '1px solid var(--color-divider)';
  }};
  background: var(--color-base-1);
  padding: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_3B};
  grid-column: span 10;
  cursor: pointer;
  color: var(--color-text-primary);
  height: ${Spacings.SPACING_14B};
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.SMALL};
`;

const StyRadio = styled(RadioButton)`
  &.MuiFormControlLabel-root {
    .MuiTypography-body1 {
      font-size: ${Fonts.SMALL};
      font-weight: ${Fonts.SEMIBOLD};
    }
  }
`;

const StyH5 = styled(H5)`
  grid-column: span 10;
  margin-bottom: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_8B};
`;

const StyDropDown = styled(Dropdown)`
  margin-bottom: ${Spacings.SPACING_40B};
  min-width: 224px;
`;

const renderTolplogies = (
  filteredTopologies,
  selectedDoubtTopology,
  onChangeTopology
) => {
  const renderTopology = filteredTopologies.map((topology) => {
    const isSelected = topology.uid === selectedDoubtTopology.uid;
    return (
      <PreferenceWrapper
        key={topology.uid}
        onClick={() => onChangeTopology(topology.uid)}
        isSelected={isSelected}
      >
        <StyRadio
          checked={isSelected}
          label={topology.name}
          key={topology.uid}
          isSelected={isSelected}
          size="small"
        />
      </PreferenceWrapper>
    );
  });
  return (
    <>
      <StyH5>Confirm subject</StyH5>
      {renderTopology}
    </>
  );
};

const ConfirmSampleDoubtDetails = ({ image, goalUID }) => {
  const { subjectAndLanguageData: subjectsAndLanguages } =
    useSubjectAndLanguages(goalUID);
  const {
    doubtConfigData: { preferredLanguage }
  } = useDoubtConfig(goalUID);
  const { topologies, languages } = subjectsAndLanguages || {};
  const filteredTopologies = topologies ? topologies.slice(1) : emptyList;
  const filteredLanguages = languages ? languages.slice(1) : emptyList;
  const [selectedDoubtTopology, setTopology] = useState(filteredTopologies[0]);
  const [selectedDoubtLanguage, setLanguage] = useState(preferredLanguage);
  const onChangeTopology = (topologyUID) => {
    const getSelectedTopology = filteredTopologies.find(
      (topology) => topology.uid === topologyUID
    );
    setTopology(getSelectedTopology);
  };

  const onChangeLanaguage = (languageUID) => {
    setLanguage(languageUID);
  };

  return (
    <>
      <StyH3>Submit doubt</StyH3>
      <DoubtImageWrapper content={image} />
      {renderTolplogies(
        filteredTopologies,
        selectedDoubtTopology,
        onChangeTopology
      )}
      <StyH5Info>Preferred solution language</StyH5Info>
      <StyDropDown
        options={filteredLanguages}
        gridColumn="span 5"
        selectedOption={selectedDoubtLanguage}
        onOptionClick={(lang) => onChangeLanaguage(lang)}
        menuWidth="224px"
        showSelectedBorder={false}
      />
    </>
  );
};

export default ConfirmSampleDoubtDetails;
